// A per-session store keeps request ownership and time validity in one place.
// It does not persist content or issue interval requests; the provider supplies
// only user-driven refresh triggers and local deadline timers.
const emptySnapshot = () => ({ items: [], banner: null, unreadCount: null, unreadCountValidUntil: null,
  nextCursor: null, loaded: false, loading: false, error: null, actionPending: false,
  detail: null, detailId: null, detailLoading: false, detailUnavailable: false,
  entityAnnouncementsEnabled: null, projectionVersion: 0 });

export class SystemAnnouncementState {
  constructor(service, { scope = null, settingEnabled = null, isSessionCurrent = () => true, monotonicNow = () => performance.now(), wallNow = Date.now } = {}) {
    this.service = service;
    this.scope = scope && Object.freeze({ ...scope });
    this.settingEnabled = settingEnabled;
    this.isSessionCurrent = isSessionCurrent;
    this.monotonicNow = monotonicNow;
    this.wallNow = wallNow;
    this.timeObservation = this.clock();
    this.listeners = new Set();
    this.controllers = new Set();
    this.generation = 0;
    this.detailGeneration = 0;
    this.lastFetch = -Infinity;
    this.anchor = null;
    this.timeUncertain = false;
    this.closed = false;
    this.snapshot = emptySnapshot();
  }
  subscribe = listener => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
  getSnapshot = () => this.snapshot;
  live = generation => !this.closed && this.isSessionCurrent() && (generation === undefined || generation === this.generation);
  emit(patch = {}) {
    if (!this.live()) return;
    this.observeElapsed();
    this.snapshot = { ...this.snapshot, ...patch };
    this.prune();
    this.listeners.forEach(listener => listener());
    this.scheduleExpiry();
  }
  now() {
    return this.anchor ? this.anchor.server + Math.max(0, this.monotonicNow() - this.anchor.mono) : Infinity;
  }
  clock() { return { mono: this.monotonicNow(), wall: this.wallNow() }; }
  elapsedUncertain(start, end = this.clock()) {
    return Math.abs((end.wall - start.wall) - (end.mono - start.mono)) > 1000;
  }
  observeElapsed() {
    // Foreground OS sleep need not emit visibilitychange, and monotonic clocks
    // can pause during sleep. Check every publication/timer and focus against a
    // wall/monotonic observation; wall-clock edits also fail closed, never add
    // life to a message. A fresh, unsuspended server response restores authority.
    if (this.elapsedUncertain(this.timeObservation)) this.timeUncertain = true;
  }
  acceptTime(asOf, requestStart) {
    const server = Date.parse(asOf);
    if (typeof asOf !== 'string' || !asOf.endsWith('Z') || !Number.isFinite(server)) throw new Error('Invalid announcement time');
    const observed = this.clock();
    const mono = observed.mono;
    // Include the full request duration conservatively. Anchoring at response
    // receipt would give a slow response a second life after server expiry.
    const upperBound = server + Math.max(0, mono - requestStart.mono);
    this.anchor = { server: Math.max(this.anchor ? this.now() : -Infinity, upperBound), mono };
    this.timeUncertain = this.elapsedUncertain(requestStart, observed);
    this.timeObservation = observed;
  }
  visible(item) {
    if (!item) return false;
    // Server audience remains authority. Fail closed if a malformed/late entity
    // payload reaches a global or disabled projection, including action results.
    if (item.entity_id != null && (this.scope?.type !== 'entity'
      || item.entity_id !== this.scope.entityId || this.snapshot.entityAnnouncementsEnabled === false)) return false;
    return item.expires_at === null || (!this.timeUncertain && Number.isFinite(Date.parse(item.expires_at)) && Date.parse(item.expires_at) > this.now());
  }
  prune() {
    const value = this.snapshot;
    value.items = value.items.filter(item => this.visible(item));
    if (value.banner && !this.visible(value.banner)) value.banner = null;
    if (value.detail && !this.visible(value.detail)) {
      value.detail = null; value.detailUnavailable = true;
    }
    if (value.unreadCountValidUntil && (this.timeUncertain || Date.parse(value.unreadCountValidUntil) <= this.now())) value.unreadCount = null;
  }
  scheduleExpiry() {
    clearTimeout(this.timer);
    if (!this.live() || this.timeUncertain || !this.anchor) return;
    const value = this.snapshot;
    const dates = [...value.items, value.banner, value.detail].filter(Boolean).map(item => item.expires_at);
    if (value.unreadCount !== null) dates.push(value.unreadCountValidUntil);
    // Include already-due deadlines: time may cross expiry after prune (for
    // example during a GC pause). Dropping them here would lose the last timer.
    const next = Math.min(...dates.filter(Boolean).map(Date.parse).filter(Number.isFinite));
    if (Number.isFinite(next)) this.timer = setTimeout(() => this.emit(), Math.min(2147483647, Math.max(1, next - this.now())));
  }
  controller() {
    const controller = new AbortController(); this.controllers.add(controller); return controller;
  }
  invalidate() {
    this.generation += 1;
    this.detailGeneration += 1;
    this.controllers.forEach(controller => controller.abort()); this.controllers.clear();
    this.feedPending = null;
    // The canceled generation can no longer publish its finally cleanup. Clear
    // its request-owned flags here so publication/withdrawal cannot strand the
    // reader in a permanently busy state; content still follows normal expiry.
    this.emit({ loading: false, actionPending: false, detailLoading: false });
  }
  settingChanged(enabled) {
    if (this.scope?.type !== 'entity' || typeof enabled !== 'boolean' || this.settingEnabled === enabled || !this.live()) return;
    const hadSetting = typeof this.settingEnabled === 'boolean';
    this.settingEnabled = enabled;
    // Context hydration and settings-refresh events are observations, not proof
    // of a change. Seed the first known value without remounting an active editor;
    // the announcement API remains authority even if cached settings disagree.
    if (!hadSetting) return;
    this.invalidate(); this.lastFetch = -Infinity;
    // An acknowledged local change retires the whole projection before refresh.
    // It cannot authorize any content; only the scoped API response can do that.
    this.emit({ ...emptySnapshot(), projectionVersion: this.snapshot.projectionVersion + 1 });
    void this.refresh({ force: true });
  }
  refresh = ({ force = false, cursor = null } = {}) => {
    if (!this.scope?.carrier || !this.live() || this.snapshot.actionPending) return Promise.resolve();
    if (this.feedPending) return this.feedPending;
    if (!force && !cursor && this.monotonicNow() - this.lastFetch < 60000) return Promise.resolve();
    let generation = this.generation;
    const scope = this.scope;
    const controller = this.controller();
    const start = this.clock(); this.lastFetch = start.mono;
    this.emit({ loading: true, error: null });
    const pending = Promise.resolve().then(() => this.live(generation) ? this.service.feed(scope, cursor, controller.signal) : null).then(async data => {
      if (!this.live(generation)) return;
      if (!Array.isArray(data.items) || !Number.isInteger(data.unreadCount) || data.unreadCount < 0) throw new Error('Invalid announcement feed');
      if (data.scope?.type !== scope.type || data.scope?.entityId !== scope.entityId
        || (scope.type === 'entity' ? typeof data.entityAnnouncementsEnabled !== 'boolean' : data.entityAnnouncementsEnabled !== null)) throw new Error('Invalid announcement scope');
      this.acceptTime(data.asOf, start);
      const interruptedDetailId = data.entityAnnouncementsEnabled === false && this.snapshot.detailLoading
        ? this.snapshot.detailId : null;
      if (data.entityAnnouncementsEnabled === false) {
        // Retire older detail/action ownership before accepting disabled evidence.
        // Keep the current feed owner so its finally can finish normally, and
        // retain only the global portion of this authorized response.
        this.invalidate(); generation = this.generation; this.feedPending = pending;
      }
      const items = cursor ? [...this.snapshot.items, ...data.items] : data.items;
      this.emit({ ...data, items: [...new Map(items.map(item => [item.id, item])).values()], loaded: true, error: null });
      // Page absence cannot prove withdrawal. Revalidate an already displayed
      // detail separately on this authorized refresh, so focus/manual recovery
      // also converges after a publisher withdraws it from another session.
      // Do not interrupt an initial detail load or repeat its read mutation.
      if (this.snapshot.detail && !this.snapshot.detailLoading) {
        await this.open(this.snapshot.detail.id, { retain: true, markRead: false });
      } else if (interruptedDetailId) {
        // A pending deep link may not yet have revealed its audience. Resolve
        // it again after revocation so it receives either global text or a 404.
        await this.open(interruptedDetailId);
      }
    }).catch(error => {
      if (this.live(generation) && !controller.signal.aborted) this.emit({ error: error.response?.data?.error?.code || 'load' });
    }).finally(() => {
      this.controllers.delete(controller);
      if (this.feedPending === pending) this.feedPending = null;
      if (this.live(generation)) this.emit({ loading: false });
    }).then(() => {
      // Return this refresh's captured owner, including its own disable transition.
      // Never substitute the store's current generation: newer work must still
      // fence the acknowledgment awaiting this reconciliation.
      return generation;
    });
    this.feedPending = pending; return pending;
  };
  open = async (id, { retain = false, markRead = true } = {}) => {
    if (!this.scope?.carrier || !this.live()) return;
    const detailGeneration = ++this.detailGeneration;
    const generation = this.generation;
    this.detailController?.abort();
    const controller = this.controller(); this.detailController = controller;
    const start = this.clock();
    this.emit({ detailId: id, detail: retain && this.snapshot.detail?.id === id ? this.snapshot.detail : null,
      detailLoading: true, detailUnavailable: false, error: null });
    try {
      const data = await this.service.detail(this.scope, id, controller.signal);
      if (!this.live(generation) || detailGeneration !== this.detailGeneration) return;
      this.acceptTime(data.asOf, start);
      this.emit({ detail: data.announcement, detailLoading: false });
      // Opening the full message is acknowledgment; merely fetching the feed,
      // rendering a badge or showing the banner must never mark it read.
      if (markRead && this.snapshot.detail && !this.snapshot.detail.read_at) await this.acknowledge(id, false);
    } catch (error) {
      if (this.live(generation) && detailGeneration === this.detailGeneration && !controller.signal.aborted) {
        const unavailable = error.response?.status === 404;
        // A transport failure cannot undo unavailability established by expiry
        // while this request was pending. Only a 404 adds new unavailable evidence.
        this.emit({ ...(unavailable ? { detail: null, detailUnavailable: true } : {}), error: unavailable ? null : 'load' });
      }
    } finally {
      this.controllers.delete(controller);
      if (this.live(generation) && detailGeneration === this.detailGeneration) this.emit({ detailLoading: false });
    }
  };
  closeDetail = () => {
    this.detailGeneration += 1; this.detailController?.abort();
    this.emit({ detailId: null, detail: null, detailLoading: false, detailUnavailable: false });
  };
  acknowledge = async (id, dismiss) => {
    if (!this.scope?.carrier || !this.live() || this.snapshot.actionPending) return;
    const interruptedDetailId = this.snapshot.detailLoading && !this.snapshot.detail ? this.snapshot.detailId : null;
    this.invalidate();
    const generation = this.generation;
    const controller = this.controller();
    const start = this.clock();
    this.emit({ actionPending: true, loading: false, detailLoading: false, error: null });
    let actionError = null;
    try {
      const data = await this.service.acknowledge(this.scope, id, dismiss, controller.signal);
      if (!this.live(generation)) return;
      this.acceptTime(data.asOf, start);
      const update = item => item?.id === id ? data.announcement : item;
      this.emit({ items: this.snapshot.items.map(update), detail: update(this.snapshot.detail),
        banner: dismiss && this.snapshot.banner?.id === id ? null : update(this.snapshot.banner), unreadCount: null });
    } catch (error) {
      if (this.live(generation) && !controller.signal.aborted) actionError = error.response?.data?.error?.code || 'action';
    } finally {
      this.controllers.delete(controller);
      if (this.live(generation)) {
        this.emit({ actionPending: false });
        const refreshedGeneration = await this.refresh({ force: true });
        if (Number.isInteger(refreshedGeneration) && this.live(refreshedGeneration)) {
          // Dismissing another banner may cancel the first load of an open deep
          // link. Restart that read under current ownership, only if the reader
          // is still on that detail. First full display still marks it read once.
          if (interruptedDetailId
            && this.snapshot.detailId === interruptedDetailId && !this.snapshot.detail) {
            await this.open(interruptedDetailId);
          }
          if (actionError && this.live(refreshedGeneration)) this.emit({ error: actionError });
        }
      }
    }
  };
  hidden() { this.observeElapsed(); }
  visibleAgain() {
    this.emit();
    return this.refresh();
  }
  dispose() {
    this.closed = true; this.invalidate(); clearTimeout(this.timer); this.listeners.clear();
  }
}
